import { type CouponIdentifiers, type CouponIndex, getCouponIndex } from '../../../../schema/coupon/entry.js';
import type {
  IDeleteCouponUseCase,
  IDeleteCouponWalletUseCase,
  IGetCouponWalletsUseCase,
  IUpdateCouponWalletUseCase,
} from '../../../domain/entry.js';

export type IDeleteCouponController = ReturnType<typeof deleteCouponController>;

type ControllerArgs = CouponIdentifiers;

/**
 * Controller for deleting a coupon.
 *
 * @param deleteCouponUseCase Use case for deleting a coupon
 * @param deleteCouponWalletUseCase Use case for deleting a coupon wallet
 * @param getCouponWalletsUseCase Use case for getting coupon wallets
 * @param updateCouponWalletUseCase Use case for updating a coupon wallet
 * @returns Controller executor function
 */
// eslint-disable-next-line @typescript-eslint/max-params
export function deleteCouponController(
  deleteCouponUseCase: IDeleteCouponUseCase,
  deleteCouponWalletUseCase: IDeleteCouponWalletUseCase,
  getCouponWalletsUseCase: IGetCouponWalletsUseCase,
  updateCouponWalletUseCase: IUpdateCouponWalletUseCase
) {
  return async function controller({ couponId, couponIndex }: ControllerArgs): Promise<boolean> {
    const isCouponDeleted = await deleteCouponUseCase.execute({ couponId, couponIndex });
    if (isCouponDeleted) {
      deleteCouponWalletUseCase.execute({ couponId, couponIndex });
    }
    // Updated index for wallets after deleted coupon index, since coupons switch index after deletion.
    const wallets = getCouponWalletsUseCase.execute({ couponId });
    if (wallets && Object.keys(wallets).length > 0) {
      for (const [index, wallet] of Object.entries(wallets)) {
        const numberIndex = Number.parseInt(index, 10);
        if (!getCouponIndex(numberIndex)) {
          continue;
        }
        if (numberIndex >= couponIndex) {
          updateCouponWalletUseCase.execute({
            couponId,
            couponIndex: (numberIndex - 1) as CouponIndex,
            payload: wallet,
          });
          deleteCouponWalletUseCase.execute({ couponId, couponIndex: numberIndex });
        }
      }
    }
    return isCouponDeleted;
  };
}
